import CreatableSelect from "react-select/creatable"
import { useMemo } from "react"
import { Spinner, Label } from "reactstrap"
import { getIn, setIn } from "formik"

const FormikCreatableSelect = ({
  options,
  name,
  formik,
  label,
  placeholder,
  loading = false,
  required = false,
  isMulti = false,
  optionBgColor,
  selectedBgColor = "#d1e7dd",
  onCreateOption,
  onChange,
}) => {
  // ✅ Get the current value from Formik using the name path
  const value = useMemo(() => getIn(formik.values, name), [formik.values, name])

  // ✅ Get the error for this field
  const error = useMemo(() => getIn(formik.errors, name), [formik.errors, name])
  const touched = useMemo(
    () => getIn(formik.touched, name),
    [formik.touched, name]
  )

  // ✅ Handle select change
  const handleChange = selected => {
    if (isMulti) {
      const values = selected ? selected.map(opt => opt.value) : []
      formik.setFieldValue(name, values)

      onChange?.(values, selected) // 👈 notify parent
    } else {
      const value = selected ? selected.value : ""
      formik.setFieldValue(name, value)

      onChange?.(value, selected) // 👈 notify parent
    }
  }

  // ✅ Handle create option
  const handleCreate = inputValue => {
    if (isMulti) {
      const currentValues = value || []
      formik.setFieldValue(name, [...currentValues, inputValue])
    } else {
      formik.setFieldValue(name, inputValue)
    }

    onCreateOption?.(inputValue)
    onChange?.(inputValue, { value: inputValue, label: inputValue }) // 👈 important
  }

  // ✅ Resolve selected value(s)
  const selectedValue = useMemo(() => {
    if (isMulti) {
      const values = value || []
      return options.filter(opt => values.includes(opt.value))
    } else {
      if (!value) return null

      // First try to find in options
      const option = options.find(opt => String(opt.value) === String(value))
      if (option) return option

      // If not found, it might be a newly created value
      // Create a temporary option for display
      return { value: value, label: value }
    }
  }, [value, options, isMulti])

  // ✅ Handle blur
  const handleBlur = () => {
    // Set touched for this specific field
    const newTouched = setIn(formik.touched, name, true)
    formik.setTouched(newTouched)
  }

  // 🎨 Styles
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? selectedBgColor
        : state.isFocused
        ? "#e9ecef"
        : optionBgColor,
      color: "#495057",
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: "38px",
      borderColor: touched && error ? "red" : "#ced4da",
      boxShadow: "none",
      backgroundColor: state.isDisabled ? "#e9ecef" : "#fff",
    }),
    menu: provided => ({ ...provided, zIndex: 9999 }),
    input: provided => ({ ...provided, backgroundColor: "transparent" }),
    singleValue: provided => ({ ...provided, backgroundColor: "transparent" }),
    multiValue: provided => ({ ...provided, backgroundColor: "#e9ecef" }),
    multiValueLabel: provided => ({ ...provided, backgroundColor: "transparent" }),
    multiValueRemove: provided => ({ ...provided, backgroundColor: "transparent" }),
  }

  const loadingMessage = () => (
    <div className="text-center py-2">
      <Spinner size="sm" /> Loading...
    </div>
  )

  return (
    <div className="mb-3">
      {label && (
        <Label className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </Label>
      )}

      <CreatableSelect
        isMulti={isMulti}
        isClearable
        inputId={name}
        options={options}
        value={selectedValue}
        placeholder={placeholder}
        onChange={handleChange}
        onCreateOption={handleCreate}
        onBlur={handleBlur}
        styles={customStyles}
        isDisabled={loading}
        noOptionsMessage={() => (loading ? loadingMessage() : "No options")}
      />

      {touched && error && (
        <div className="text-danger small mt-1">{error}</div>
      )}
    </div>
  )
}

export default FormikCreatableSelect
